/* ============================================
   ROULOTTES LUPIEN - THEME JS
   ============================================ */

(function() {
  'use strict';

  /* ============================================
     UTILS
     ============================================ */

  const debounce = (fn, delay = 300) => {
    let timer;
    return function(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  };

  const formatMoney = (cents) => {
    return new Intl.NumberFormat('fr-CA', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'CAD',
      minimumFractionDigits: 2
    }).format(cents / 100);
  };

  /* ============================================
     PREDICTIVE SEARCH
     ============================================ */

  class PredictiveSearch {
    constructor() {
      this.input = document.querySelector('[data-rl-search-input]');
      this.results = document.querySelector('[data-rl-search-results]');
      if (!this.input || !this.results) return;

      this.isEn = (document.documentElement.lang || 'fr').toLowerCase().indexOf('en') === 0;
      this.root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      this.lastLogged = '';
      this.logTimer = null;

      this.input.addEventListener('input', debounce(this.search.bind(this), 250));
      this.input.addEventListener('focus', () => {
        if (this.input.value.length >= 2) this.results.classList.add('is-open');
      });
      document.addEventListener('click', (e) => {
        if (!e.target.closest('.rl-search')) this.results.classList.remove('is-open');
      });

      // When a suggestion is clicked, put a real /search results page into history
      // first, so the browser Back button returns to the results — not whatever
      // page the shopper searched from (often the homepage).
      this.results.addEventListener('click', (e) => {
        const link = e.target.closest('a.rl-search-result');
        if (!link) return; // "See all results" + empty-state links already point to /search
        const q = this.input.value.trim();
        if (!q) return;
        e.preventDefault();
        const searchUrl = `${this.root}search?q=${encodeURIComponent(q)}`;
        try { history.pushState(null, '', searchUrl); } catch (_) {}
        window.location.assign(link.href);
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (query.length < 2) {
        this.results.classList.remove('is-open');
        this.results.innerHTML = '';
        return;
      }
      try {
        const response = await fetch(
           `${this.root}search/suggest.json?q=${encodeURIComponent(query)}` +
           `&resources[type]=product,collection` +
           `&resources[limit]=8` +
           `&resources[options][unavailable_products]=last` +
           `&resources[options][fields]=title,product_type,variants.title,variants.sku,variants.barcode,vendor`
         );
        const data = await response.json();
        const results = data.resources.results;
        const count = (results.products || []).length + (results.collections || []).length;
        this.render(results);
        this.scheduleLog(query, count);
      } catch (err) { console.error('Search error:', err); }
    }

    // Log the FINAL typed query (+ result count) once the user settles — captures
    // intent and zero-result misses without spamming every prefix.
    scheduleLog(query, count) {
      clearTimeout(this.logTimer);
      this.logTimer = setTimeout(() => {
        if (query.length < 3 || query === this.lastLogged) return;
        this.lastLogged = query;
        try {
          const m = document.cookie.match(/_shopify_y=([^;]+)/);
          const body = JSON.stringify({
            event_type: 'search_submitted',
            visitorId: m ? decodeURIComponent(m[1]) : null,
            source_site: 'boutique',
            occurredAt: new Date().toISOString(),
            raw: { query: query.slice(0, 200), results: count, source: 'predictive' }
          });
          const url = 'https://kbukewyucxydkzwohtvc.supabase.co/functions/v1/track-commerce-event';
          if (navigator.sendBeacon && navigator.sendBeacon(url, body)) return;
          fetch(url, { method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' }, body, keepalive: true });
        } catch (e) {}
      }, 1400);
    }

    render(results) {
      const products = results.products || [];
      const collections = results.collections || [];

      if (products.length === 0 && collections.length === 0) {
        this.results.innerHTML = this.emptyState();
        this.results.classList.add('is-open');
        return;
      }

      let html = '';
      if (collections.length > 0) {
        html += '<div style="padding:8px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;font-weight:600;color:#6B7280;background:#F8F9FA;">' + (this.isEn ? 'Categories' : 'Catégories') + '</div>';
        collections.forEach(c => {
          html += `<a href="${c.url}" class="rl-search-result">
            <div style="width:32px;height:32px;background:#FFF2E5;border-radius:4px;display:flex;align-items:center;justify-content:center;"><i class="ti ti-category" style="color:#ED6400;"></i></div>
            <div style="flex:1;"><div class="rl-search-result-title">${c.title}</div></div>
          </a>`;
        });
      }
      if (products.length > 0) {
        html += '<div style="padding:8px 12px;font-size:11px;text-transform:uppercase;letter-spacing:0.06em;font-weight:600;color:#6B7280;background:#F8F9FA;">' + (this.isEn ? 'Products' : 'Produits') + '</div>';
        products.forEach(p => {
          const price = p.price ? formatMoney(parseFloat(p.price) * 100) : '';
          html += `<a href="${p.url}" class="rl-search-result">
            <img src="${p.featured_image?.url || ''}" alt="" class="rl-search-result-img" loading="lazy" onerror="this.style.background='#F1F3F5'; this.src='';" />
            <div style="flex:1;min-width:0;">
              <div class="rl-search-result-title" style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.title}</div>
              ${p.vendor ? `<div style="font-size:11px;color:#6B7280;">${p.vendor}</div>` : ''}
            </div>
            <div class="rl-search-result-price">${price}</div>
          </a>`;
        });
      }
      html += `<a href="${this.root}search?q=${encodeURIComponent(this.input.value)}" style="display:block;padding:12px;text-align:center;font-size:13px;font-weight:600;color:#ED6400;border-top:1px solid #E5E7EB;">${this.isEn ? 'See all results' : 'Voir tous les résultats'} →</a>`;

      this.results.innerHTML = html;
      this.results.classList.add('is-open');
    }

    emptyState() {
      const q = this.input.value;
      const telLink = document.querySelector('a[href^="tel:"]');
      const tel = telLink ? telLink.getAttribute('href') : 'tel:18888974949';
      const phone = telLink ? telLink.textContent.trim() : '1-888-897-4949';
      const t = this.isEn
        ? { none: 'No match for', tip: 'Try the brand, part number, or fewer words.', all: 'See all results', help: "Can't find your part? Call", special: "— we'll find it or special-order it." }
        : { none: 'Aucun résultat pour', tip: 'Essayez la marque, le numéro de pièce ou moins de mots.', all: 'Voir tous les résultats', help: 'Vous ne trouvez pas votre pièce ? Appelez', special: '— on la trouve ou on la commande.' };
      return '<div style="padding:18px 16px;text-align:center;">' +
        '<div style="font-size:14px;color:#374151;font-weight:600;">' + t.none + ' « ' + this.escape(q) + ' »</div>' +
        '<div style="font-size:12px;color:#6B7280;margin-top:4px;">' + t.tip + '</div>' +
        '<a href="' + this.root + 'search?q=' + encodeURIComponent(q) + '" style="display:inline-block;margin-top:10px;font-size:13px;font-weight:600;color:#ED6400;">' + t.all + ' →</a>' +
        '<div style="margin-top:14px;padding-top:12px;border-top:1px solid #E5E7EB;font-size:13px;color:#374151;">' + t.help + ' <a href="' + tel + '" style="font-weight:700;color:#173461;white-space:nowrap;">' + this.escape(phone) + '</a> ' + t.special + '</div>' +
      '</div>';
    }

    escape(s) { const d = document.createElement('div'); d.textContent = String(s == null ? '' : s); return d.innerHTML; }
  }

  /* ============================================
     CART
     ============================================ */

  class Cart {
    constructor() {
      this.countEl = document.querySelector('[data-rl-cart-count]');
      this.bindEvents();
      this.refresh();
    }

    bindEvents() {
      document.addEventListener('click', async (e) => {
        const addBtn = e.target.closest('[data-rl-add-to-cart]');
        if (addBtn) {
          e.preventDefault();
          const variantId = addBtn.dataset.variantId;
          const qty = parseInt(addBtn.dataset.quantity || '1', 10);
          await this.addItem(variantId, qty, addBtn);
        }

        const removeBtn = e.target.closest('[data-rl-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          await this.removeItem(removeBtn.dataset.lineKey);
        }
      });

      // Quantity changes on cart page
      document.querySelectorAll('[data-rl-cart-qty]').forEach(input => {
        input.addEventListener('change', async (e) => {
          await this.updateItem(e.target.dataset.lineKey, parseInt(e.target.value, 10));
        });
      });
    }

    async addItem(variantId, quantity = 1, btn = null) {
      if (btn) {
        btn.disabled = true;
        const originalHtml = btn.innerHTML;
        btn.innerHTML = '<i class="ti ti-loader"></i> Ajout...';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: Number(variantId), quantity })
          });

          if (!response.ok) throw new Error('Cart add failed');

          await this.refresh();
          this.showToast('Produit ajouté au panier');

          btn.innerHTML = '<i class="ti ti-check"></i> Ajouté !';
          setTimeout(() => {
            btn.innerHTML = originalHtml;
            btn.disabled = false;
          }, 1500);
        } catch (err) {
          console.error(err);
          btn.innerHTML = originalHtml;
          btn.disabled = false;
          this.showToast('Erreur — réessayez', 'error');
        }
      }
    }

    async removeItem(lineKey) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 })
        });
        window.location.reload();
      } catch (err) { console.error(err); }
    }

    async updateItem(lineKey, quantity) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity })
        });
        window.location.reload();
      } catch (err) { console.error(err); }
    }

    async refresh() {
      try {
        const response = await fetch('/cart.js');
        const cart = await response.json();
        if (this.countEl) {
          this.countEl.textContent = cart.item_count;
          this.countEl.style.display = cart.item_count > 0 ? 'flex' : 'none';
        }
      } catch (err) { console.error(err); }
    }

    showToast(message, type = 'success') {
      const existing = document.querySelector('.rl-toast');
      if (existing) existing.remove();

      const toast = document.createElement('div');
      toast.className = 'rl-toast';
      toast.style.cssText = `
        position: fixed; bottom: 20px; right: 20px;
        background: ${type === 'error' ? '#EF4444' : '#10B981'};
        color: white; padding: 12px 20px; border-radius: 8px;
        font-size: 14px; font-weight: 500; z-index: 9999;
        box-shadow: 0 8px 24px rgba(0,0,0,0.15);
        display: flex; align-items: center; gap: 8px;
        animation: rl-toast-in 0.3s ease;
      `;
      toast.innerHTML = `<i class="ti ti-${type === 'error' ? 'alert-circle' : 'check'}"></i> ${message}`;

      if (!document.querySelector('#rl-toast-style')) {
        const style = document.createElement('style');
        style.id = 'rl-toast-style';
        style.textContent = '@keyframes rl-toast-in { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }';
        document.head.appendChild(style);
      }

      document.body.appendChild(toast);
      setTimeout(() => toast.remove(), 3000);
    }
  }

  /* ============================================
     PRODUCT PAGE
     ============================================ */

  class ProductPage {
    constructor() {
      this.gallery = document.querySelector('[data-rl-gallery]');
      this.qtyInputs = document.querySelectorAll('[data-rl-qty]');
      this.stickyBuy = document.querySelector('[data-rl-sticky-buy]');
      this.addToCart = document.querySelector('[data-rl-add-to-cart]');

      if (this.gallery) this.initGallery();
      if (this.qtyInputs.length) this.initQty();
      if (this.stickyBuy && this.addToCart) this.initStickyBuy();
      this.initTabs();
      this.initVariants();
    }

    initGallery() {
      const main = this.gallery.querySelector('.rl-gallery-main img');
      const thumbs = this.gallery.querySelectorAll('.rl-gallery-thumb');

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          thumbs.forEach(t => t.classList.remove('is-active'));
          thumb.classList.add('is-active');
          if (main) main.src = thumb.dataset.fullImage || thumb.querySelector('img').src;
        });
      });
    }

    initQty() {
      this.qtyInputs.forEach(wrapper => {
        const input = wrapper.querySelector('input');
        const minus = wrapper.querySelector('[data-rl-qty-minus]');
        const plus = wrapper.querySelector('[data-rl-qty-plus]');

        if (minus) minus.addEventListener('click', () => {
          const v = parseInt(input.value, 10) || 1;
          if (v > 1) {
            input.value = v - 1;
            this.syncQty();
          }
        });
        if (plus) plus.addEventListener('click', () => {
          input.value = (parseInt(input.value, 10) || 1) + 1;
          this.syncQty();
        });
        if (input) input.addEventListener('change', () => this.syncQty());
      });
    }

    syncQty() {
      const qty = document.querySelector('[data-rl-qty] input')?.value || 1;
      if (this.addToCart) this.addToCart.dataset.quantity = qty;
      if (this.stickyBuy) {
        const stickyBtn = this.stickyBuy.querySelector('[data-rl-add-to-cart]');
        if (stickyBtn) stickyBtn.dataset.quantity = qty;
      }
    }

    initStickyBuy() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.stickyBuy.classList.remove('is-visible');
            document.body.classList.remove('rl-sticky-buy-active');
          } else {
            this.stickyBuy.classList.add('is-visible');
            document.body.classList.add('rl-sticky-buy-active');
          }
        });
      }, { threshold: 0 });

      observer.observe(this.addToCart);
    }

    initTabs() {
      const tabs = document.querySelectorAll('[data-rl-tab]');
      const panels = document.querySelectorAll('[data-rl-tab-panel]');

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const target = tab.dataset.rlTab;
          tabs.forEach(t => t.classList.remove('is-active'));
          panels.forEach(p => p.style.display = 'none');
          tab.classList.add('is-active');
          const panel = document.querySelector(`[data-rl-tab-panel="${target}"]`);
          if (panel) panel.style.display = 'block';
        });
      });
    }

    initVariants() {
      const variantOptions = document.querySelectorAll('[data-rl-variant-option]');
      variantOptions.forEach(opt => {
        opt.addEventListener('click', () => {
          const group = opt.closest('[data-rl-variant-group]');
          if (group) {
            group.querySelectorAll('[data-rl-variant-option]').forEach(o => o.classList.remove('is-selected'));
          }
          opt.classList.add('is-selected');
          const currentLabel = group ? group.querySelector('[data-rl-variant-current]') : null;
          if (currentLabel) currentLabel.textContent = opt.dataset.value;
          this.updateSelectedVariant();
        });
      });
    }

    updateSelectedVariant() {
      // Logic: find variant matching all selected options
      // Update price, stock, add to cart button
      const selected = document.querySelectorAll('[data-rl-variant-option].is-selected');
      const values = Array.from(selected).map(s => s.dataset.value);
      const variantData = window.rlProductData?.variants;
      if (!variantData) return;

      const match = variantData.find(v =>
        v.options.every((opt, i) => opt === values[i])
      );

      if (match && this.addToCart) {
        this.addToCart.dataset.variantId = match.id;

        // Route B pricing — mirror the automatic discount for DISPLAY ONLY.
        // The real price (match.price) is never lowered here, so checkout still
        // applies the automatic discount exactly once (no double-stacking).
        const promoPct = window.rlProductData?.discountPercent || 0;
        const base = match.price;
        let displayPrice = base;
        let compareVal = 0;
        let savePct = 0;
        if (promoPct > 0) {
          displayPrice = Math.round(base * (100 - promoPct) / 100);
          compareVal = base;
          savePct = promoPct;
        } else if (match.compareAtPrice && match.compareAtPrice > base) {
          compareVal = match.compareAtPrice;
          savePct = Math.round((match.compareAtPrice - base) * 100 / match.compareAtPrice);
        }

        const priceEl = document.querySelector('[data-rl-price]');
        if (priceEl) {
          priceEl.textContent = formatMoney(displayPrice);
          priceEl.dataset.rlBasePrice = base;
        }
        const compareEl = document.querySelector('[data-rl-price-compare]');
        if (compareEl) {
          if (compareVal > 0) {
            compareEl.textContent = formatMoney(compareVal);
            compareEl.style.display = '';
          } else {
            compareEl.textContent = '';
            compareEl.style.display = 'none';
          }
        }
        const saveEl = document.querySelector('[data-rl-price-save]');
        if (saveEl) {
          if (compareVal > 0) {
            const tpl = saveEl.dataset.rlSaveTemplate || '';
            const amountStr = formatMoney(compareVal - displayPrice);
            saveEl.textContent = `-${savePct}% ${tpl.replace('XXAMTXX', amountStr)}`;
            saveEl.style.display = '';
          } else {
            saveEl.textContent = '';
            saveEl.style.display = 'none';
          }
        }

        // Swap the gallery to the selected variant's image (if one is assigned)
        if (match.featured_image) {
          const mainImg = document.querySelector('.rl-gallery-main img');
          if (mainImg) mainImg.src = match.featured_image;
          document.querySelectorAll('.rl-gallery-thumb').forEach(t => {
            const full = t.dataset.fullImage || '';
            t.classList.toggle('is-active', full.split('?')[0] === match.featured_image.split('?')[0]);
          });
        }

        // SKU — met à jour tous les emplacements (meta + specs) et masque si vide
        document.querySelectorAll('[data-rl-sku]').forEach(el => { el.textContent = match.sku || ''; });
        const skuWrap = document.querySelector('[data-rl-sku-wrap]');
        if (skuWrap) skuWrap.style.display = match.sku ? '' : 'none';

        // Keep the mobile sticky buy bar in sync with the selected variant
        if (this.stickyBuy) {
          const stickyPrice = this.stickyBuy.querySelector('.rl-sticky-buy-price');
          if (stickyPrice) stickyPrice.textContent = formatMoney(displayPrice);
          const stickyBtn = this.stickyBuy.querySelector('[data-rl-add-to-cart]');
          if (stickyBtn) {
            stickyBtn.dataset.variantId = match.id;
            stickyBtn.dataset.quantity = this.addToCart.dataset.quantity || '1';
            stickyBtn.disabled = !match.available;
          }
        }

        const stockEl = document.querySelector('[data-rl-stock]');
        if (stockEl) {
          if (match.available) {
            stockEl.className = 'rl-stock-banner in-stock';
            stockEl.innerHTML = '<i class="ti ti-check"></i> En stock — Livraison rapide';
            this.addToCart.disabled = false;
          } else {
            stockEl.className = 'rl-stock-banner out-of-stock';
            stockEl.innerHTML = '<i class="ti ti-x"></i> Rupture de stock';
            this.addToCart.disabled = true;
          }
        }
      }
    }
  }

  /* ============================================
     SHIPPING ESTIMATOR
     ============================================ */

  class ShippingEstimator {
    constructor() {
      this.form = document.querySelector('[data-rl-shipping-form]');
      if (!this.form) return;
      this.form.addEventListener('submit', this.estimate.bind(this));
    }

    estimate(e) {
      e.preventDefault();
      const isEn = (document.documentElement.lang || 'fr').toLowerCase().indexOf('en') === 0;
      const input = this.form.querySelector('input[name="postal_code"]');
      const resultEl = this.form.querySelector('[data-rl-shipping-result]');
      const postal = (input.value || '').trim().toUpperCase();

      if (!postal.match(/^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/)) {
        resultEl.innerHTML = '<span style="color:#EF4444;">' + (isEn ? 'Invalid postal code' : 'Code postal invalide') + '</span>';
        return;
      }

      // --- CONFIG (adjust if your real free-shipping minimum is $100, not $99) ---
      const FREE_SHIP_MIN = 99;
      const DG_FROM = 30;
      const HEAVY_FROM = { QC: 49.95, ON: 64.95, MAR: 79.95, NL: 104.95, PRA: 109.95, BC: 134.95, NORTH: 249.95, OTHER: 64.95 };

      const L = postal.charAt(0);
      let zone = 'OTHER';
      if ('GHJ'.indexOf(L) > -1) zone = 'QC';
      else if ('KLMNP'.indexOf(L) > -1) zone = 'ON';
      else if ('BCE'.indexOf(L) > -1) zone = 'MAR';
      else if (L === 'A') zone = 'NL';
      else if ('RST'.indexOf(L) > -1) zone = 'PRA';
      else if (L === 'V') zone = 'BC';
      else if ('XY'.indexOf(L) > -1) zone = 'NORTH';

      const days = {
        QC: isEn ? '1–2 business days' : '1 à 2 jours ouvrables',
        ON: isEn ? '2–4 business days' : '2 à 4 jours ouvrables',
        MAR: isEn ? '3–5 business days' : '3 à 5 jours ouvrables',
        NL: isEn ? '4–7 business days' : '4 à 7 jours ouvrables',
        PRA: isEn ? '3–5 business days' : '3 à 5 jours ouvrables',
        BC: isEn ? '4–6 business days' : '4 à 6 jours ouvrables',
        NORTH: isEn ? '5–10 business days' : '5 à 10 jours ouvrables',
        OTHER: isEn ? '4–7 business days' : '4 à 7 jours ouvrables'
      }[zone];

      const pd = window.rlProductData || {};
      const money = (n) => '$' + n.toFixed(2);

      // Batteries : livrables au Québec seulement. Hors-QC → aucun tarif, ramassage en magasin.
      if (pd.isQuebecOnly && zone !== 'QC') {
        resultEl.innerHTML =
          '<div style="display:flex;align-items:flex-start;gap:6px;color:#B45309;font-weight:500;">' +
            '<i class="ti ti-map-pin-off" style="margin-top:2px;"></i><span>' +
            (isEn
              ? 'Ships within <strong>Quebec only</strong> — cannot be delivered to your region. <strong>Free in-store pickup</strong> available.'
              : 'Livré au <strong>Québec seulement</strong> — ne peut pas être expédié dans votre région. <strong>Ramassage en magasin</strong> gratuit.') +
            '</span>' +
          '</div>';
        return;
      }

      let costLine, color = '#047857';

      if (pd.isDangerousGoods) {
        color = '#B45309';
        costLine = isEn
          ? 'Shipping from <strong>' + money(DG_FROM) + '</strong> (dangerous-goods handling included) — or <strong>free in-store pickup</strong>'
          : 'Livraison à partir de <strong>' + money(DG_FROM) + '</strong> (manutention de marchandises dangereuses incluse) — ou <strong>ramassage gratuit en magasin</strong>';
      } else if (pd.isBulky) {
        color = '#B45309';
        const from = HEAVY_FROM[zone];
        costLine = isEn
          ? 'Heavy/oversized item — shipping from <strong>' + money(from) + '</strong> to your region (final amount depends on weight, shown at checkout) — or <strong>free in-store pickup</strong>'
          : 'Article volumineux/lourd — livraison à partir de <strong>' + money(from) + '</strong> dans votre région (montant final selon le poids, affiché au paiement) — ou <strong>ramassage gratuit en magasin</strong>';
      } else {
        costLine = isEn
          ? 'Free shipping on orders over <strong>$' + FREE_SHIP_MIN + '</strong>'
          : 'Livraison gratuite dès <strong>' + FREE_SHIP_MIN + ' $</strong>';
      }

      resultEl.innerHTML =
        '<div style="display:flex;align-items:flex-start;gap:6px;color:' + color + ';font-weight:500;">' +
          '<i class="ti ti-truck" style="margin-top:2px;"></i><span>' + days + ' — ' + costLine + '</span>' +
        '</div>';
    }
  }

  /* ============================================
     MOBILE FILTERS
     ============================================ */

  class MobileFilters {
    constructor() {
      this.btn = document.querySelector('[data-rl-mobile-filter-btn]');
      this.panel = document.querySelector('[data-rl-filters]');
      if (!this.btn || !this.panel) return;

      // Backdrop overlay (created once)
      this.overlay = document.createElement('div');
      this.overlay.className = 'rl-filters-overlay';
      document.body.appendChild(this.overlay);

      this.open = this.open.bind(this);
      this.close = this.close.bind(this);

      this.btn.addEventListener('click', this.open);
      this.overlay.addEventListener('click', this.close);

      const closeBtn = this.panel.querySelector('[data-rl-filters-close]');
      if (closeBtn) closeBtn.addEventListener('click', this.close);

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    open() {
      this.panel.classList.add('is-open');
      this.overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.panel.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }
  }

  /* ============================================
     MOBILE NAVIGATION (hamburger + accordéon sous-menus)
     ============================================ */

  class MobileNav {
    constructor() {
      this.hamburger = document.querySelector('[data-rl-menu-toggle]');
      this.nav = document.querySelector('[data-rl-nav]');
      this.toggles = document.querySelectorAll('[data-rl-submenu-toggle]');

      // Hamburger ouvre/ferme tout le menu mobile
      if (this.hamburger && this.nav) {
        this.hamburger.addEventListener('click', (e) => {
          e.preventDefault();
          const isOpen = this.nav.classList.toggle('is-open');
          this.hamburger.setAttribute('aria-expanded', String(isOpen));
        });
      }

      // Chaque parent avec sous-menu a son propre toggle accordéon
      this.toggles.forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleSubmenu(btn);
        });
      });
    }

    toggleSubmenu(btn) {
      const isOpen = btn.getAttribute('aria-expanded') === 'true';

      // Ferme les autres sous-menus ouverts (un seul à la fois)
      this.toggles.forEach((other) => {
        if (other !== btn) other.setAttribute('aria-expanded', 'false');
      });

      btn.setAttribute('aria-expanded', String(!isOpen));
    }
  }

  /* ============================================
     GOOGLE REVIEWS (Places API)
     Fetch + cache localStorage 24h
     ============================================ */

  class GoogleReviews {
    constructor() {
      this.ratingEl = document.querySelector('[data-rl-google-rating]');
      this.testimonialsEl = document.querySelector('[data-rl-testimonials]');
      if (!this.ratingEl) return;

      this.apiKey = this.ratingEl.dataset.apiKey;
      this.placeId = this.ratingEl.dataset.placeId;
      if (!this.apiKey || !this.placeId) return;

      this.cacheKey = `rl_google_reviews_${this.placeId}`;
      this.cacheTTL = 24 * 60 * 60 * 1000; // 24h

      this.load();
    }

    async load() {
      try {
        const cached = this.readCache();
        if (cached) {
          this.render(cached);
          return;
        }
        const data = await this.fetch();
        this.writeCache(data);
        this.render(data);
      } catch (err) {
        console.warn('Google Reviews failed:', err);
        // Fail silently - testimonials hardcoded en blocks restent visibles
      }
    }

    readCache() {
      try {
        const raw = localStorage.getItem(this.cacheKey);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (Date.now() - parsed.timestamp > this.cacheTTL) {
          localStorage.removeItem(this.cacheKey);
          return null;
        }
        return parsed.data;
      } catch {
        return null;
      }
    }

    writeCache(data) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({
          timestamp: Date.now(),
          data: data
        }));
      } catch {
        // localStorage plein ou désactivé - on ignore
      }
    }

    async fetch() {
      // Google Places API v1 - endpoint Place Details
      const url = `https://places.googleapis.com/v1/places/${this.placeId}?fields=rating,userRatingCount,reviews&key=${this.apiKey}&languageCode=fr`;
      const response = await fetch(url, {
        headers: { 'Accept-Language': document.documentElement.lang || 'fr' }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.json();
    }

    render(data) {
      // Rating + nombre d'avis
      const rating = data.rating || 0;
      const count = data.userRatingCount || 0;

      const valueEl = this.ratingEl.querySelector('[data-rl-rating-value]');
      const starsEl = this.ratingEl.querySelector('[data-rl-rating-stars]');
      const countEl = this.ratingEl.querySelector('[data-rl-rating-count]');

      if (valueEl) valueEl.textContent = rating.toFixed(1);
      if (countEl) countEl.textContent = count.toLocaleString();
      if (starsEl) starsEl.innerHTML = this.renderStars(rating);

      // Témoignages dynamiques
      if (this.testimonialsEl && Array.isArray(data.reviews)) {
        const dynamicReviews = data.reviews.slice(0, 3).map(r => this.renderTestimonial(r)).join('');
        // Préfixer aux témoignages hardcodés existants
        this.testimonialsEl.insertAdjacentHTML('afterbegin', dynamicReviews);
      }
    }

    renderStars(rating) {
      const full = Math.floor(rating);
      const half = rating - full >= 0.5;
      const empty = 5 - full - (half ? 1 : 0);
      let html = '';
      for (let i = 0; i < full; i++) html += '<i class="ti ti-star-filled"></i>';
      if (half) html += '<i class="ti ti-star-half-filled"></i>';
      for (let i = 0; i < empty; i++) html += '<i class="ti ti-star"></i>';
      return html;
    }

    renderTestimonial(review) {
      const stars = this.renderStars(review.rating || 5);
      const authorName = (review.authorAttribution && review.authorAttribution.displayName) || 'Client Google';
      const authorPhoto = (review.authorAttribution && review.authorAttribution.photoUri) || '';
      const initial = authorName.charAt(0).toUpperCase();
      const text = (review.text && review.text.text) || (review.originalText && review.originalText.text) || '';
      // Tronquer à ~200 char pour des cards uniformes
      const truncated = text.length > 220 ? text.slice(0, 217) + '...' : text;

      const avatarHTML = authorPhoto
        ? `<img src="${authorPhoto}" alt="${this.escapeHTML(authorName)}" loading="lazy" width="40" height="40" referrerpolicy="no-referrer">`
        : `<div class="rl-testimonial-avatar">${this.escapeHTML(initial)}</div>`;

      return `
        <article class="rl-testimonial rl-testimonial--google">
          <div class="rl-testimonial-stars">${stars}</div>
          <p class="rl-testimonial-quote">"${this.escapeHTML(truncated)}"</p>
          <div class="rl-testimonial-author">
            ${avatarHTML}
            <div>
              <div class="rl-testimonial-name">${this.escapeHTML(authorName)}</div>
              <div class="rl-testimonial-location"><i class="ti ti-brand-google"></i> Avis Google</div>
            </div>
          </div>
        </article>
      `;
    }

    escapeHTML(str) {
      if (str == null) return '';
      const div = document.createElement('div');
      div.textContent = String(str);
      return div.innerHTML;
    }
  }

  /* ============================================
     HOURS DROPDOWN (onglets Pièces / Service)
     ============================================ */

  class HoursDropdown {
    constructor() {
      this.wrapper = document.querySelector('[data-rl-hours]');
      if (!this.wrapper) return;

      this.toggle = this.wrapper.querySelector('[data-rl-hours-toggle]');
      this.dropdown = this.wrapper.querySelector('[data-rl-hours-dropdown]');
      this.tabs = this.wrapper.querySelectorAll('[data-rl-hours-tab]');
      this.panels = this.wrapper.querySelectorAll('[data-rl-hours-panel]');
      if (!this.toggle || !this.dropdown) return;

      // Ouverture / fermeture au clic
      this.toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.toggleOpen();
      });

      // Switch d'onglets
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', (e) => {
          e.stopPropagation();
          this.switchTab(tab.dataset.rlHoursTab);
        });
      });

      // Fermeture au clic extérieur
      document.addEventListener('click', (e) => {
        if (!this.wrapper.contains(e.target)) this.close();
      });

      // Fermeture sur Échap
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    }

    toggleOpen() {
      const isOpen = this.dropdown.classList.contains('is-open');
      if (isOpen) this.close();
      else this.open();
    }

    open() {
      this.dropdown.classList.add('is-open');
      this.toggle.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.dropdown.classList.remove('is-open');
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    switchTab(name) {
      this.tabs.forEach((tab) => {
        const active = tab.dataset.rlHoursTab === name;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', String(active));
      });
      this.panels.forEach((panel) => {
        const active = panel.dataset.rlHoursPanel === name;
        panel.classList.toggle('is-active', active);
        panel.hidden = !active;
      });
    }
  }

  /* ============================================
     PRODUCT RECOMMENDATIONS (AJAX)
     Charge les recommandations via l'API Shopify
     ============================================ */

  class ProductRecommendations {
    constructor() {
      this.container = document.querySelector('[data-rl-recommendations]');
      if (!this.container) return;

      this.sectionId = this.container.dataset.sectionId;
      this.productId = this.container.dataset.productId;
      this.limit = this.container.dataset.limit || 4;
      this.intent = this.container.dataset.intent || 'related';

      if (!this.productId) return;

      // Si le serveur a déjà rendu des recommandations (performed?), ne rien faire
      if (this.container.querySelector('.rl-recommendations-inner')) return;

      this.load();
    }

    async load() {
      try {
        const url = `${window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/'}recommendations/products` +
          `?section_id=${encodeURIComponent(this.sectionId)}` +
          `&product_id=${encodeURIComponent(this.productId)}` +
          `&limit=${encodeURIComponent(this.limit)}` +
          `&intent=${encodeURIComponent(this.intent)}`;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const text = await response.text();

        // La réponse est le HTML complet de la section. On extrait le contenu utile.
        const html = new DOMParser().parseFromString(text, 'text/html');
        const fresh = html.querySelector('[data-rl-recommendations]');

        if (fresh && fresh.querySelector('.rl-recommendations-inner')) {
          this.container.innerHTML = fresh.innerHTML;
        }
        // Sinon: aucune recommandation disponible, on laisse le conteneur vide
      } catch (err) {
        console.warn('Product recommendations failed:', err);
        // Fail silently
      }
    }
  }

  /* ============================================
     INIT
     ============================================ */

  document.addEventListener('DOMContentLoaded', () => {
    new PredictiveSearch();
    new Cart();
    new ProductPage();
    new ShippingEstimator();
    new MobileFilters();
    new MobileNav();
    new GoogleReviews();
    new HoursDropdown();
    new ProductRecommendations();
  });

})();
